import { defineComponent as _defineComponent } from 'vue'
import { PropType, computed } from 'vue';
import { defineMessages } from '@/lib/i18n';
import {
  SdsEditHistory,
  SdsEditHistoryDetail,
} from '@/interfaces/model/general/sds';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import SdsEditHistoryEntry from './SdsEditHistoryEntry.vue';
import { now } from '@/utils/datetime';
import {
  groupingRules,
  ignoreDeleteEntities,
} from '@/lib/sds-edit-history/rule';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsTableEditHistory',
  props: {
  isActiveFeature: {
    type: Boolean,
    default: true,
  },
  sdsEditHistories: {
    type: Array as PropType<Array<SdsEditHistory>>,
    required: true,
    default: () => [],
  },
},
  setup(__props) {

const props = __props;



const $t = i18n.t.bind(i18n);

const m = defineMessages({
  editHistory: '編集履歴',
  noticeMessage:
    '「SDS変更履歴表示オプション」を追加購入いただいたお客様がご利用になれる機能です。お求めの場合は「お問い合わせ」からご連絡ください。',
  noticeEmpty: '編集履歴がありません',
  exampleFirstName: '太郎',
  exampleLastName: '山田',
  exampleInsertValue: '株式会社ケミカン',
  exampleUpdateColumnName: 'ガスボンベ(gas)',
  exampleDeleteValue: '2-メトキシ-2-メチルプロパン(MTBE)',
});

const actionOrder: Record<string, number> = {
  insert: 0,
  update: 1,
  delete: 2,
};

const editHistories = computed(() => {
  const { sdsEditHistories, isActiveFeature } = props;
  const currentDateTime = now().toISO({ includeOffset: false });
  if (!isActiveFeature) {
    return {
      sdsEditHistories: [
        {
          uuid: '',
          sds_id: '',
          user: {
            uuid: '',
            first_name: String($t(m.exampleFirstName)),
            last_name: String($t(m.exampleLastName)),
            middle_name: '',
          },
          edited_at: currentDateTime,
          created_at: currentDateTime,
          updated_at: currentDateTime,
          details: [
            {
              uuid: '',
              sds_product: null,
              section: 1,
              edit_entity: 'section1_manufacturer',
              action: 'insert',
              raw_column_name: 'name',
              column_name: String($t(sharedMessages.manufacturers).valueOf()),
              column_type: 'string',
              before_value: '',
              after_value: String($t(m.exampleInsertValue).valueOf()),
              value_prefix: '',
            },
            {
              uuid: '',
              sds_product: null,
              section: 1,
              edit_entity: 'section2_label',
              action: 'update',
              raw_column_name: 'gas',
              column_name: String($t(m.exampleUpdateColumnName).valueOf()),
              column_type: 'boolean',
              before_value: 'false',
              after_value: 'true',
              value_prefix: '',
            },
            {
              uuid: '',
              sds_product: null,
              section: 1,
              edit_entity: 'section8_acgih_material',
              action: 'delete',
              raw_column_name: 'name',
              column_name: '',
              column_type: 'string',
              before_value: String($t(m.exampleDeleteValue).valueOf()),
              after_value: '',
              value_prefix: '',
            },
          ],
        },
      ],
    };
  }

  const newSdsEditHistories: Array<SdsEditHistory> = [];
  for (const history of sdsEditHistories) {
    const allDetails = history.details.reduce((acc, detail) => {
      acc[detail.uuid] = detail;
      return acc;
    }, {} as Record<string, SdsEditHistoryDetail>);

    // Edit history grouped by product. Most SDS have no per-product
    // scoping (sds_product is null on every detail), so those details are
    // bucketed together under NO_PRODUCT_KEY instead of being dropped --
    // otherwise they'd never reach the entity-grouping pass below and a
    // single material add would show one history line per changed column.
    const NO_PRODUCT_KEY = '__no_product__';
    const productGroupedEditHistories = history.details.reduce(
      (acc, detail) => {
        const productId = detail.sds_product
          ? detail.sds_product.id
          : NO_PRODUCT_KEY;
        acc[productId] = acc[productId] || [];
        acc[productId].push(detail);
        return acc;
      },
      {} as Record<string, Array<SdsEditHistoryDetail>>
    );

    // Process edit history by product
    for (const productId in productGroupedEditHistories) {
      const productDetails = productGroupedEditHistories[productId];

      // Group by edit entity
      const groupedEntityEditHistories = productDetails.reduce(
        (acc, detail) => {
          const editEntity = detail.edit_entity;
          acc[editEntity] = acc[editEntity] || [];
          acc[editEntity].push(detail);
          return acc;
        },
        {} as Record<string, Array<SdsEditHistoryDetail>>
      );

      // Process edit history based on grouping rules
      for (const editEntity in groupedEntityEditHistories) {
        if (!groupingRules[editEntity]) continue;

        const details = groupedEntityEditHistories[editEntity];
        const editEntities = groupingRules[editEntity].editEntities;

        // Process details with prefixes in actions other than update
        details.forEach((detail) => {
          if (
            !allDetails[detail.uuid] ||
            detail.action === 'update' ||
            !detail.value_prefix
          )
            return;

          const prefix = detail.value_prefix;
          // Process the details of the related editing entity
          editEntities.forEach((relatedEntity) => {
            const relatedDetails =
              groupedEntityEditHistories[relatedEntity] || [];
            relatedDetails.forEach((relatedDetail) => {
              if (
                relatedDetail.uuid !== detail.uuid &&
                relatedDetail.value_prefix === prefix
              ) {
                // section15_material's own pass (editEntity === 'section15_material')
                // never removes its CAS entries - a CAS number carries real
                // chemical-identity info that should stay visible as its own
                // line even when it merges with its parent material. The law
                // row's own pass (editEntity === 'section15') is different:
                // the law, material and CAS are always entered as one set, so
                // the law's own merge does fold CAS away too.
                if (
                  editEntity === 'section15' ||
                  relatedDetail.edit_entity !== 'section15_cas'
                ) {
                  delete allDetails[relatedDetail.uuid];
                }
              }
            });
          });
        });
      }
    }

    // Sort details by action and section
    const newDetails = Object.values(allDetails).sort((a, b) => {
      const actionDiff = actionOrder[a.action] - actionOrder[b.action];
      return actionDiff !== 0 ? actionDiff : a.section - b.section;
    });

    const filteredDetails = newDetails.filter((detail) => {
      if (detail.action === 'delete') {
        return !ignoreDeleteEntities.includes(detail.edit_entity);
      }
      if (detail.action === 'update') {
        return detail.before_value !== detail.after_value;
      }
      return true;
    });

    newSdsEditHistories.push({
      ...history,
      details: filteredDetails,
    });
  }

  return {
    sdsEditHistories: newSdsEditHistories,
  };
});

return { __sfc: true,props, $t, m, actionOrder, editHistories, SdsEditHistoryEntry }
}

})